import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://alohai-prod.william-4c8.workers.dev';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (userData) => api.post('/api/auth/signup', userData),
  login: (credentials) => api.post('/api/auth/login', credentials),
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },
};

// Boards API
export const boardsAPI = {
  getBoards: () => api.get('/api/boards'),
  createBoard: (boardData) => api.post('/api/boards', boardData),
  updateBoard: (id, boardData) => api.put(`/api/boards/${id}`, boardData),
  deleteBoard: (id) => api.delete(`/api/boards/${id}`),
  getBoardItems: (boardId) => api.get(`/api/boards/${boardId}/items`),
};

// Content API
export const contentAPI = {
  addContent: (contentData) => api.post('/api/content/add', contentData),
  uploadFile: (formData) => api.post('/api/content/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  saveGenerated: (contentData) => api.post('/api/content/save-generated', contentData),
  summarizeContent: (itemId, options) => api.post(`/api/content/summarize/${itemId}`, { options }),
};

// AI API
export const aiAPI = {
  generateScript: (data) => api.post('/api/ai/generate-script', data),
  analyzeTone: (data) => api.post('/api/ai/analyze-tone', data),
  repurposeContent: (data) => api.post('/api/ai/repurpose', data),
};

// Analytics API
export const analyticsAPI = {
  trackMetrics: (contentId, contentType, metrics) => 
    api.post('/api/analytics/track', { contentId, contentType, metrics }),
  getDashboard: (timeRange = '30d') => 
    api.get(`/api/analytics/dashboard/${timeRange}`),
  generateInsights: () => 
    api.post('/api/analytics/insights/generate'),
  getInsights: (limit = 10) => 
    api.get(`/api/analytics/insights?limit=${limit}`),
  getSettings: () => 
    api.get('/api/analytics/settings'),
  updateSettings: (settings) => 
    api.put('/api/analytics/settings', settings),
};

// Utility functions
export const isAuthenticated = () => {
  return !!localStorage.getItem('authToken');
};

export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const setAuthData = (token, user) => {
  localStorage.setItem('authToken', token);
  localStorage.setItem('user', JSON.stringify(user));
};

export default api; 